import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Layers,
  BookOpen,
  ClipboardCheck,
  Cable,
  Landmark,
  Ruler,
  BadgeCheck,
  SearchCheck,
  MapPin,
  Building2,
  Zap,
  Calendar,
  ShieldCheck,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { SampleReport, SampleSection } from "@/data/sample-reports";

const ICONS = {
  layers: Layers,
  book: BookOpen,
  clipboard: ClipboardCheck,
  plug: Cable,
  landmark: Landmark,
  ruler: Ruler,
  badge: BadgeCheck,
  search: SearchCheck,
} as const;

interface Props {
  report: SampleReport;
}

const SampleReportView = ({ report }: Props) => {
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(report.sections.map((s) => [s.id, true]))
  );

  const totalPoints = useMemo(
    () => report.sections.reduce((n, s) => n + s.dataPoints.length, 0),
    [report]
  );

  const allOpen = report.sections.every((s) => open[s.id]);
  const toggleAll = () =>
    setOpen(Object.fromEntries(report.sections.map((s) => [s.id, !allOpen])));

  return (
    <div className="min-h-screen bg-fordje-gray-light/40">
      <Helmet>
        <title>{`${report.prospect} sample report | Fordje`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="description"
          content={`Sample Fordje jurisdiction report prepared for ${report.prospect}.`}
        />
      </Helmet>

      <div className="container-custom py-8 md:py-12">
        {/* Branded site header */}
        <div className="mb-6 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-3 text-fordje-navy hover:opacity-80 transition-opacity"
            aria-label="Fordje home"
          >
            <img
              src="/lovable-uploads/595a50f5-c631-4403-8464-cad23f811cfe.png"
              alt=""
              className="h-8 w-8"
            />
            <span className="font-ubuntu text-xl font-bold">Fordje</span>
          </a>
          <a
            href="/"
            className="text-sm font-medium text-fordje-blue hover:text-fordje-navy transition-colors"
          >
            Back to fordje.com
          </a>
        </div>

        {/* Report card */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-fordje-navy via-fordje-blue to-fordje-blue/30" />

          <div className="flex items-center justify-between px-6 md:px-8 py-3 text-[11px] tracking-[0.18em] uppercase text-muted-foreground border-b">
            <span className="font-mono"># {report.reportId}</span>
            <span>Confidential · Prepared by Fordje</span>
          </div>

          <div className="px-6 md:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6 justify-between">
              <div className="flex gap-4 min-w-0">
                <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-fordje-blue/10 text-fordje-blue">
                  <Sparkles className="h-7 w-7" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-fordje-blue mb-2">
                    {report.kicker}
                  </p>
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-fordje-navy leading-[1.1]">
                    {report.title}
                  </h1>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Sample prepared for{" "}
                    <a
                      href={report.prospectUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="font-medium text-fordje-blue hover:underline"
                    >
                      {report.prospect}
                    </a>
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-fordje-blue/25 bg-fordje-blue/5 px-5 py-4 text-center shrink-0">
                <div className="flex items-center justify-center gap-2 text-fordje-navy font-semibold tracking-wide">
                  <ShieldCheck className="h-4 w-4 text-fordje-blue" />
                  VERIFIED BY FORDJE
                </div>
                <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  {report.verifiedLabel}
                </p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Meta icon={MapPin} label="Jurisdiction" value={report.jurisdiction} sub={report.city} />
              <Meta icon={Building2} label="Project type" value={report.projectType} sub={report.systemSize} />
              <Meta icon={Zap} label="System / Utility" value={report.utility} />
              <Meta icon={Calendar} label="Report run" value={report.reportRun} />
            </div>
          </div>
        </div>


        {/* Body */}
        <div className="mt-8 grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)_280px]">
          {/* Sections nav */}
          <aside className="hidden lg:block">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
              Sections
            </p>
            <nav className="space-y-1">
              {report.sections.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white transition-colors"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-white text-fordje-blue">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] text-muted-foreground">{s.number}</span>
                      <span className="block text-sm font-medium text-fordje-navy leading-snug">
                        {s.title}
                      </span>
                      <span className="block text-[11px] text-muted-foreground">
                        {s.dataPoints.length} pts
                      </span>
                    </span>
                  </a>
                );
              })}
            </nav>
          </aside>

          {/* Main */}
          <main className="min-w-0">
            <div className="flex items-center justify-between mb-3 text-sm text-muted-foreground">
              <span>
                {report.sections.length} sections · {totalPoints} data points
              </span>
              <button onClick={toggleAll} className="hover:text-fordje-blue transition-colors">
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            </div>

            <div className="space-y-4">
              {report.sections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  open={!!open[section.id]}
                  onToggle={() =>
                    setOpen((p) => ({ ...p, [section.id]: !p[section.id] }))
                  }
                />
              ))}
            </div>

            <div className="mt-8 rounded-xl border bg-white p-6">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Sources
              </p>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">
                {report.footnotes.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ol>
            </div>
          </main>

          {/* Right rail */}
          <aside className="space-y-4">
            <div className="rounded-xl border bg-white p-5">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Jurisdiction snapshot
              </p>
              <div className="space-y-4">
                <Snapshot icon={MapPin} label="Jurisdiction" value={report.jurisdiction} />
                {report.county && <Snapshot icon={Building2} label="County" value={report.county} />}
                <Snapshot icon={Zap} label="Utility" value={report.utility} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const Meta = ({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="flex items-start gap-3">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-white text-fordje-blue">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="font-semibold text-fordje-navy leading-snug">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

const Snapshot = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) => (
  <div className="flex items-start gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-white text-fordje-blue">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-fordje-navy leading-snug">{value}</p>
    </div>
  </div>
);

const SectionCard = ({
  section,
  open,
  onToggle,
}: {
  section: SampleSection;
  open: boolean;
  onToggle: () => void;
}) => {
  const Icon = ICONS[section.icon];
  return (
    <section id={section.id} className="scroll-mt-24 rounded-xl border bg-white overflow-hidden">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-5 md:px-6 py-5 text-left"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-fordje-blue/10 text-fordje-blue">
          <Icon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Section {section.number}
          </span>
          <span className="block text-xl font-semibold text-fordje-navy">{section.title}</span>
        </span>
        <span className="hidden sm:block text-sm text-muted-foreground">
          {section.dataPoints.length} data points
        </span>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div className="border-t divide-y">
          {section.dataPoints.map((dp, i) => (
            <div key={i} className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] px-5 md:px-6 py-5">
              <p className="font-medium text-fordje-navy leading-snug">{dp.label}</p>
              <div className="min-w-0">
                {dp.value && <p className="text-fordje-navy break-words">{dp.value}</p>}
                {dp.bullets && (
                  <ul className={cn("space-y-1 text-fordje-navy list-disc pl-4", dp.value && "mt-2")}>
                    {dp.bullets.map((b, bi) => (
                      <li key={bi} className="leading-snug break-words">{b}</li>
                    ))}
                  </ul>
                )}
                {dp.codeSection && (
                  <p className="mt-2 text-xs text-muted-foreground font-mono">{dp.codeSection}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SampleReportView;
